import React, { useState, useEffect, useMemo } from 'react';
import ActionHistory from './ActionHistory';
import CardSelector from './CardSelector';
import SetupModal from './SetupModal';
import GameSummaryBar from './GameSummaryBar';
import { POSITIONS, SUITS, RANKS, ACTIONS, STREETS } from '../constants';
import ShareButton from './ShareButton';
import ReactDOM from 'react-dom';
import HandHistoryCard from './HandHistoryCard';
import ExportButton from './ExportButton';
import CanvasCardRenderer from './CanvasCardRenderer';
import ExportSelector from './ExportSelector';
import AmountModal from './AmountModal';
import { decodeGameState } from '../utils/shareUtils';
import { useAppContext } from '../contexts/AppContext';
import { LanguageSelector } from './';
import { useTranslation } from 'react-i18next';
const holdemhandranker = require('holdemhandranker');

// 添加自定义样式
const customStyles = `
  /* 使用本地字体文件 */
  @import url('/fonts/fonts.css');

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  
  @keyframes slideIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
  
  .animate-slideIn {
    animation: slideIn 0.3s ease-out forwards;
  }
  
  .modal-content {
    transform-origin: center;
  }
  
  .backdrop-blur-sm {
    backdrop-filter: blur(4px);
  }

  /* 高级应用名称样式 */
  .app-header {
    padding: 2rem 0;
    position: relative;
    overflow: hidden;
    background-image: 
      radial-gradient(circle at 10% 10%, rgba(172, 50, 228, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 90% 90%, rgba(121, 24, 242, 0.05) 0%, transparent 50%);
    border-radius: 12px;
    margin-bottom: 2rem;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.03);
  }

  @media (max-width: 768px) {
    .app-header {
      padding: 1rem 0;
      margin-bottom: 1rem;
    }
  }
  
  @keyframes subtleFloat {
    0% { transform: translateY(0) translateX(0); }
    25% { transform: translateY(-0.5px) translateX(0.5px); }
    50% { transform: translateY(0) translateX(1px); }
    75% { transform: translateY(0.5px) translateX(0.5px); }
    100% { transform: translateY(0) translateX(0); }
  }
  
  .subtle-float {
    animation: subtleFloat 8s ease-in-out infinite;
  }
  
  .title-wrapper {
    position: relative;
    display: inline-block;
    transition: all 0.3s ease;
  }
  
  .title-wrapper:hover .app-title {
    filter: drop-shadow(0 2px 4px rgba(172, 50, 228, 0.7));
    transform: perspective(500px) rotateX(2deg) scale(1.02);
    letter-spacing: 1.5px;
  }
  
  .title-shadow {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .title-glow {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    filter: blur(16px);
    opacity: 0.2;
    background: radial-gradient(circle at center, #AC32E4, #7918F2 50%, transparent 80%);
    z-index: -1;
  }

  /* Logo Styles */
  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .logo .main {
    font-size: 36px;
    font-weight: 700;
    background: linear-gradient(to right, #FF0844, #FFB199);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    margin-right: 8px;
  }
  
  .logo .pro {
    font-size: 36px;
    font-weight: 700;
    background: linear-gradient(to right, #3a36e0, #AC32E4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  @media (max-width: 768px) {
    .logo .main,
    .logo .pro {
      font-size: 28px;
    }
  }
  
  .logo-box {
    padding: 18px 32px;
    background: linear-gradient(to right, rgba(255, 8, 68, 0.05), rgba(58, 54, 224, 0.05));
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    position: relative;
  }

  @media (max-width: 768px) {
    .logo-box {
      padding: 12px 24px;
    }
  }
  
  .logo-box:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    background: linear-gradient(to right, rgba(255, 8, 68, 0.08), rgba(58, 54, 224, 0.08));
  }
  
  /* Dark mode styles */
  @media (prefers-color-scheme: dark) {
    .logo-box {
      background: linear-gradient(to right, rgba(255, 8, 68, 0.15), rgba(58, 54, 224, 0.15));
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }
    
    .logo-box:hover {
      background: linear-gradient(to right, rgba(255, 8, 68, 0.2), rgba(58, 54, 224, 0.2));
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    }
  }
`;

// CSS 动画样式
const animationStyles = `
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  
  @keyframes slideIn {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
  
  .animate-fadeOut {
    animation: fadeOut 0.3s ease-in forwards;
  }
  
  .animate-slideIn {
    animation: slideIn 0.5s ease-out forwards;
  }
  
  /* 添加新动画 */
  @keyframes popFadeIn {
    0% { opacity: 0; transform: scale(0.8) translateY(5px); }
    50% { opacity: 1; transform: scale(1.05) translateY(-2px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  
  .animate-popFadeIn {
    animation: popFadeIn 0.4s ease-out forwards;
  }

  /* 应用名称的特效 */
  @keyframes titleGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .app-title {
    background: linear-gradient(202deg, #AC32E4 0%, #7918F2 48%, #4801FF 100%);
    background-size: 200% auto;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    animation: titleGradient 6s linear infinite;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    letter-spacing: 1px;
    padding: 0.3rem;
    font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
    font-weight: 800;
    filter: drop-shadow(0 1px 1px rgba(172, 50, 228, 0.5));
    transform: perspective(500px) rotateX(5deg);
    transform-origin: center center;
  }
  
  .app-title-underline {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa, #3b82f6, #2563eb);
    background-size: 200% auto;
    animation: titleGradient 6s linear infinite;
    transform-origin: left;
    transform: scaleX(1);
  }
`;

const PokerHandFormatter = () => {
  // 从上下文中获取应用名称和版权信息
  const { appName, copyright } = useAppContext();
  
  const [lastAggressivePlayer, setLastAggressivePlayer] = useState(null);
  const [gameInfo, setGameInfo] = useState({
    stakes: '1/2',
    gameType: 'No-Limit Hold\'em',
    players: 9,
    effectiveStack: 0,
  });
  const [heroInfo, setHeroInfo] = useState({
    position: '',
    hand: ['', ''],
  });
  const [streetActions, setStreetActions] = useState({
    preflop: [],
    flop: [],
    turn: [],
    river: [],
  });
  const [board, setBoard] = useState({
    flop: ['', '', ''],
    turn: [''],
    river: [''],
  });
  const [currentStreet, setCurrentStreet] = useState('preflop');
  const [currentPlayer, setCurrentPlayer] = useState('');
  const [formattedHand, setFormattedHand] = useState('');
  const [potSize, setPotSize] = useState(0);
  const [lastBetSize, setLastBetSize] = useState(0);
  const [actionAmount, setActionAmount] = useState('');
  const [usedCards, setUsedCards] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [playerPositions, setPlayerPositions] = useState([]);
  const [gameEndedAtStreet, setGameEndedAtStreet] = useState(null);
  const [playerStacks, setPlayerStacks] = useState({});

  const [showHeroSelector, setShowHeroSelector] = useState(false);
  const [showFlopSelector, setShowFlopSelector] = useState(false);
  const [showTurnSelector, setShowTurnSelector] = useState(false);
  const [showRiverSelector, setShowRiverSelector] = useState(false);

  const [playersHands, setPlayersHands] = useState({});
  const [showdownMode, setShowdownMode] = useState(false);
  const [currentShowdownPlayer, setCurrentShowdownPlayer] = useState(null);
  const [showPlayerSelector, setShowPlayerSelector] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(true);
  const [setupComplete, setSetupComplete] = useState(false);
  const [currentCardSelection, setCurrentCardSelection] = useState(null);
  const [amountModalOpen, setAmountModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const isMobile = window.innerWidth < 768;
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const { t } = useTranslation();

  // Load state from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encodedState = params.get('action');
    
    if (encodedState) {
      const decodedState = decodeGameState(encodedState);
      if (decodedState) {
        // First set all the basic state
        setGameInfo(decodedState.gameInfo);
        setHeroInfo(decodedState.heroInfo);
        setBoard(decodedState.board);
        setCurrentStreet(decodedState.currentStreet);
        setLastBetSize(decodedState.lastBetSize);
        setActivePlayers(decodedState.activePlayers);
        setLastAggressivePlayer(decodedState.lastAggressivePlayer);
        setPlayersHands(decodedState.playersHands);
        setGameEndedAtStreet(decodedState.gameEndedAtStreet);
        setStreetActions(decodedState.streetActions);
        setPotSize(decodedState.potSize);
        
        // Disable initial setup since we're loading from URL
        setSetupComplete(true);
        setShowSetupModal(false);

        // Set player positions
        setPlayerPositions(POSITIONS[decodedState.gameInfo.players]);
      }
    }
  }, []);

  // Remove all other useEffects that set potSize
  useEffect(() => {
    if (heroInfo.position && playerPositions.length > 0 && !window.location.search.includes('action=')) {
      setActivePlayers([...playerPositions]);
      setCurrentPlayer('UTG');
      const smallBlind = parseFloat(gameInfo.stakes.split('/')[0]);
      const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);
      setPotSize(smallBlind + bigBlind);
    }
  }, [heroInfo.position, playerPositions, gameInfo.stakes]);

  // Remove the duplicate useEffect for setting pot size
  useEffect(() => {
    if (gameInfo.players >= 6 && gameInfo.players <= 9) {
      setPlayerPositions(POSITIONS[gameInfo.players]);
    }
  }, [gameInfo.players]);

  // Initialize player stacks when setup is complete
  const handleSetupComplete = () => {
    setShowSetupModal(false);
    setSetupComplete(true);

    if (heroInfo.position && playerPositions.length > 0 && !window.location.search.includes('action=')) {
      setActivePlayers([...playerPositions]);
      setCurrentPlayer('UTG');
      const smallBlind = parseFloat(gameInfo.stakes.split('/')[0]);
      const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);
      setPotSize(smallBlind + bigBlind);
      setLastBetSize(bigBlind);

      // Initialize player stacks
      const initialStacks = {};
      playerPositions.forEach(pos => {
        initialStacks[pos] = gameInfo.effectiveStack;
      });
      // Deduct blinds from SB and BB
      initialStacks['SB'] = gameInfo.effectiveStack - smallBlind;
      initialStacks['BB'] = gameInfo.effectiveStack - bigBlind;
      setPlayerStacks(initialStacks);
    }
  };

  const [latestAction, setLatestAction] = useState(null);

  const handleGameInfoChange = (e) => {
    setGameInfo({ ...gameInfo, [e.target.name]: e.target.value });
  };

  const handleHeroInfoChange = (e) => {
    setHeroInfo({ ...heroInfo, [e.target.name]: e.target.value });
  };

  const handleHeroHandChange = (index, value) => {
    const newHand = [...heroInfo.hand];
    newHand[index] = value;
    setHeroInfo({ ...heroInfo, hand: newHand });
  };

  const handleBoardChange = (street, index, value) => {
    setBoard(prevBoard => {
      const newBoard = { ...prevBoard };
      const oldCard = newBoard[street][index];
      newBoard[street][index] = value;

      setUsedCards(prevUsedCards => {
        const newUsedCards = [...prevUsedCards];
        if (oldCard) {
          const index = newUsedCards.indexOf(oldCard);
          if (index > -1) {
            newUsedCards.splice(index, 1);
          }
        }
        if (value) {
          newUsedCards.push(value);
        }
        return newUsedCards;
      });

      return newBoard;
    });
  };

  const getNextPlayer = (currentPosition) => {
    const currentIndex = playerPositions.indexOf(currentPosition);

    for (let i = 1; i <= playerPositions.length; i++) {
      const nextIndex = (currentIndex + i) % playerPositions.length;
      const nextPlayer = playerPositions[nextIndex];

      if (activePlayers.includes(nextPlayer)) {
        return nextPlayer;
      }
    }

    return activePlayers[0];
  };

  const selectWinner = (player) => {
    setWinner(player);
    setGameOver(true);
    formatHand(true, player);
  };

  const isStreetComplete = (actions) => {
    if (actions.length < 1) return false;

    const lastAggressiveActionIndex = actions.findLastIndex(action =>
      action.action === 'bet' || action.action === 'raise'
    );

    if (lastAggressiveActionIndex === -1) {
      const actedPlayers = new Set();
      actions.forEach(action => {
        actedPlayers.add(action.player);
      });

      return activePlayers.every(player => actedPlayers.has(player));
    }

    const lastAggressivePlayer = actions[lastAggressiveActionIndex].player;
    const actionsAfterAggressive = actions.slice(lastAggressiveActionIndex + 1);

    const remainingPlayers = activePlayers.filter(player =>
      player !== lastAggressivePlayer
    );

    const allPlayersResponded = remainingPlayers.every(player => {
      const playerActionAfterBet = actionsAfterAggressive.find(action =>
        action.player === player
      );

      return playerActionAfterBet &&
        (playerActionAfterBet.action === 'fold' ||
          playerActionAfterBet.action === 'call');
    });

    return allPlayersResponded;
  };

  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  const addAction = (action) => {
    console.log(`Before action - Active players: ${activePlayers.join(', ')}`);
    console.log(`Current action: ${currentPlayer} ${action}`);

    if (!heroInfo.position) {
      alert('Please set Hero position before taking any actions.');
      return;
    }

    if (action === 'bet' || action === 'raise') {
      setCurrentAction(action);
      setAmountModalOpen(true);
      return;
    }

    executeAction(action);
  };

  // 检查是否所有活跃玩家都all-in了
  const areAllPlayersAllIn = (activePlayersList, stacks) => {
    return activePlayersList.every(player => stacks[player] === 0);
  };

  const executeAction = (action, amount = null) => {
    let actionAmt = 0;
    let newPotSize = potSize;
    let newActivePlayers = [...activePlayers];
    let newPlayerStacks = { ...playerStacks };

    if (action === 'fold') {
      newActivePlayers = activePlayers.filter(player => player !== currentPlayer);
      setActivePlayers(newActivePlayers);

      if (newActivePlayers.length === 1) {
        setGameOver(true);
        setWinner(newActivePlayers[0]);
        setGameEndedAtStreet(currentStreet);
        formatHand(true, newActivePlayers[0]);
        return;
      }
    }

    if (action === 'bet' || action === 'raise') {
      if (amount === null) {
        alert('Please enter a valid amount for bet or raise');
        return;
      }

      // Check if amount exceeds player's remaining stack
      if (amount > playerStacks[currentPlayer]) {
        // Convert to all-in
        actionAmt = playerStacks[currentPlayer];
        action = 'allin';
      } else {
        actionAmt = amount;
      }
      newPotSize += actionAmt;
      newPlayerStacks[currentPlayer] -= actionAmt;
      setLastBetSize(actionAmt);
    }

    if (action === 'call') {
      let callAmount = 0;
      if (currentStreet === 'preflop') {
        const smallBlind = parseFloat(gameInfo.stakes.split('/')[0]);
        const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);
        
        const playerPreviousAction = streetActions[currentStreet].find(
          action => action.player === currentPlayer
        );

        if (playerPreviousAction) {
          callAmount = lastBetSize - playerPreviousAction.amount;
        } else if (currentPlayer === 'SB') {
          callAmount = lastBetSize - smallBlind;
        } else if (currentPlayer === 'BB') {
          callAmount = lastBetSize - bigBlind;
        } else if (lastBetSize > 0) {
          callAmount = lastBetSize;
        } else {
          callAmount = bigBlind;
        }
      } else {
        callAmount = lastBetSize;
      }

      // Check if call amount exceeds player's remaining stack
      if (callAmount > playerStacks[currentPlayer]) {
        // Convert to all-in
        actionAmt = playerStacks[currentPlayer];
        action = 'allin';
      } else {
        actionAmt = callAmount;
      }
      newPotSize += actionAmt;
      newPlayerStacks[currentPlayer] -= actionAmt;
    }

    const newStreetActions = { ...streetActions };
    let actionToAdd = {
      player: currentPlayer,
      action,
      amount: action === 'check' || action === 'fold' ? 0 : actionAmt,
      potSize: newPotSize,
      timestamp: new Date().getTime()
    };

    newStreetActions[currentStreet].push(actionToAdd);
    setStreetActions(newStreetActions);
    setLatestAction(actionToAdd);
    setPlayerStacks(newPlayerStacks);

    if (action === 'bet' || action === 'raise' || action === 'allin') {
      setPotSize(newPotSize);
      setLastBetSize(actionAmt);
      setLastAggressivePlayer(currentPlayer);
    } else if (action === 'call') {
      setPotSize(newPotSize);
    }

    const isCurrentStreetComplete = isStreetComplete(newStreetActions[currentStreet]);

    // 检查是否所有玩家都all-in了
    const allPlayersAllIn = areAllPlayersAllIn(newActivePlayers, newPlayerStacks);

    if (!gameOver) {
      if (allPlayersAllIn) {
        // 如果所有玩家都all-in了，先让用户选择剩余的公共牌
        if (currentStreet === 'preflop') {
          setCurrentStreet('flop');
          setCurrentCardSelection('flop');
        } else if (currentStreet === 'flop') {
          setCurrentStreet('turn');
          setCurrentCardSelection('turn');
        } else if (currentStreet === 'turn') {
          setCurrentStreet('river');
          setCurrentCardSelection('river');
        } else if (currentStreet === 'river') {
          // 所有公共牌都选择完毕，进入showdown模式
          setShowdownMode(true);
          const initialHands = {};
          initialHands[heroInfo.position] = [...heroInfo.hand];
          setPlayersHands(initialHands);

          const otherActivePlayers = newActivePlayers.filter(p => p !== heroInfo.position);
          if (otherActivePlayers.length > 0) {
            setCurrentShowdownPlayer(otherActivePlayers[0]);
            setCurrentCardSelection('player');
          }
        }
      } else if (isCurrentStreetComplete) {
        if (currentStreet === 'river') {
          setShowdownMode(true);
          const initialHands = {};
          initialHands[heroInfo.position] = [...heroInfo.hand];
          setPlayersHands(initialHands);

          const otherActivePlayers = newActivePlayers.filter(p => p !== heroInfo.position);
          if (otherActivePlayers.length > 0) {
            setCurrentShowdownPlayer(otherActivePlayers[0]);
            setCurrentCardSelection('player');
          }
        } else {
          const nextStreet = STREETS[STREETS.indexOf(currentStreet) + 1];
          setCurrentStreet(nextStreet);
          setLastBetSize(0);
          setLastAggressivePlayer(null);

          if (nextStreet === 'flop') {
            setCurrentCardSelection('flop');
          } else if (nextStreet === 'turn') {
            setCurrentCardSelection('turn');
          } else if (nextStreet === 'river') {
            setCurrentCardSelection('river');
          }

          const nextStreetFirstActor = getFirstActorForStreet(nextStreet, newActivePlayers);
          setCurrentPlayer(nextStreetFirstActor);
        }
      } else {
        const nextPlayer = getNextActivePlayer(currentPlayer, newActivePlayers);
        setCurrentPlayer(nextPlayer);
      }
    }

    console.log({
      actionAdded: actionToAdd,
      newActivePlayers: newActivePlayers.join(', '),
      currentStreet,
      potSize: newPotSize,
      lastBetSize: action === 'bet' || action === 'raise' ? actionAmt : lastBetSize,
      isStreetComplete: isCurrentStreetComplete,
      nextPlayer: currentPlayer,
      playerStacks: newPlayerStacks,
      allPlayersAllIn
    });
  };

  const handleAmountConfirm = (amount) => {
    setAmountModalOpen(false);
    executeAction(currentAction, amount);
    setCurrentAction(null);
  };

  const getFirstActorForStreet = (street, activePlayersList) => {
    if (street === 'preflop') {
      return activePlayersList.includes('UTG') ? 'UTG' : getNextActivePlayer('BTN', activePlayersList);
    } else {
      const orderAfterBTN = ['SB', 'BB', 'UTG', 'UTG+1', 'UTG+2', 'LJ', 'HJ', 'CO', 'BTN'];
      return orderAfterBTN.find(pos => activePlayersList.includes(pos)) || activePlayersList[0];
    }
  };

  const getNextActivePlayer = (currentPosition, activePlayersList) => {
    const currentIndex = playerPositions.indexOf(currentPosition);

    for (let i = 1; i <= playerPositions.length; i++) {
      const nextIndex = (currentIndex + i) % playerPositions.length;
      const nextPlayer = playerPositions[nextIndex];

      if (activePlayersList.includes(nextPlayer)) {
        return nextPlayer;
      }
    }

    return activePlayersList[0];
  };

  const moveToNextStreet = (newActivePlayers) => {
    const _activePlayers = newActivePlayers || activePlayers;
    const currentIndex = STREETS.indexOf(currentStreet);

    if (currentIndex < STREETS.length - 1) {
      const nextStreet = STREETS[currentIndex + 1];
      setCurrentStreet(nextStreet);

      setLastBetSize(0);
      setLastAggressivePlayer(null);

      setActivePlayers(_activePlayers);

      const firstActor = getFirstActorForStreet(nextStreet, _activePlayers);
      setCurrentPlayer(firstActor);

      setBoard(prevBoard => {
        const newBoard = { ...prevBoard };
        if (nextStreet === 'flop' && newBoard.flop.every(card => card === '')) {
          newBoard.flop = ['', '', ''];
        } else if (nextStreet === 'turn' && newBoard.turn[0] === '') {
          newBoard.turn = [''];
        } else if (nextStreet === 'river' && newBoard.river[0] === '') {
          newBoard.river = [''];
        }
        return newBoard;
      });
    }
  };

  const formatHand = (_gameover, _winner) => {
    let formatted = `${gameInfo.stakes} ${gameInfo.gameType} (${gameInfo.players} handed)\n\n`;

    const heroHandFormatted = heroInfo.hand.map(card => {
      if (!card) return '';
      const rank = card.slice(0, -1);
      const suit = card.slice(-1);
      return rank + convertSuitToSymbol(suit);
    }).join('');

    formatted += `Pre-flop: Hero is ${heroInfo.position} with ${heroHandFormatted}\n`;

    let currentPot = 0;
    const smallBlind = parseFloat(gameInfo.stakes.split('/')[0]);
    const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);

    currentPot += smallBlind + bigBlind;

    STREETS.forEach(street => {
      if (streetActions[street].length > 0) {
        if (street !== 'preflop') {
          formatted += `\n${street.charAt(0).toUpperCase() + street.slice(1)}: `;
          if (street === 'flop') {
            const formattedFlop = board.flop.map(card => {
              if (!card) return '';
              const rank = card.slice(0, -1);
              const suit = card.slice(-1);
              return rank + convertSuitToSymbol(suit);
            }).join('');
            formatted += `${formattedFlop}\n`;
          } else if (street === 'turn') {
            if (board.turn[0]) {
              const rank = board.turn[0].slice(0, -1);
              const suit = board.turn[0].slice(-1);
              formatted += `${rank}${convertSuitToSymbol(suit)}\n`;
            } else {
              formatted += '\n';
            }
          } else if (street === 'river') {
            if (board.river[0]) {
              const rank = board.river[0].slice(0, -1);
              const suit = board.river[0].slice(-1);
              formatted += `${rank}${convertSuitToSymbol(suit)}\n`;
            } else {
              formatted += '\n';
            }
          }
        }

        // 检查是否有任何raise或bet动作
        const hasRaiseOrBet = street === 'preflop' && streetActions[street].some(
          a => (a.action === 'bet' || a.action === 'raise') && streetActions[street].indexOf(a) < streetActions[street].length - 1
        );

        streetActions[street].forEach((action, index) => {
          let actionText = `${action.player} `;
          
          // 调整动作文本并首字母大写
          if (street === 'preflop' && !hasRaiseOrBet && action.action === 'call') {
            actionText += 'Limp';
          } else if (street === 'preflop' && !hasRaiseOrBet && action.action === 'raise' && index === 0) {
            actionText += 'Open';
          } else if (action.action === 'allin') {
            actionText += 'All-in';  // 修改 allin 的显示格式
          } else {
            // 其他动作首字母大写
            actionText += action.action.charAt(0).toUpperCase() + action.action.slice(1);
          }

          if (action.action === 'bet' || action.action === 'raise' || action.action === 'open' || action.action === 'allin') {
            currentPot += action.amount;
            actionText += ` ${action.amount}`;
          } else if (action.action === 'call' || action.action === 'limp') {
            let callAmount = action.amount;
            if (!callAmount && callAmount !== 0) {
              callAmount = lastBetSize;
              if (street === 'preflop' && action.player === 'SB') {
                const smallBlind = parseFloat(gameInfo.stakes.split('/')[0]);
                const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);
                callAmount = bigBlind - smallBlind;
              }
            }
            currentPot += callAmount;
            actionText += ` ${callAmount}`;
          }

          if (action.action === 'bet' || action.action === 'raise' || action.action === 'call' || 
              action.action === 'open' || action.action === 'limp' || action.action === 'allin') {
            actionText += ` (Pot: ${currentPot})`;
          }

          formatted += actionText + '\n';
        });
      }
    });

    // Add showdown information if available
    if (showdownMode && Object.keys(playersHands).length > 0) {
      formatted += '\nShowdown:\n';
      Object.entries(playersHands).forEach(([player, cards]) => {
        const formattedCards = cards.map(card => {
          const rank = card.slice(0, -1);
          const suit = card.slice(-1);
          return rank + convertSuitToSymbol(suit);
        }).join('');
        formatted += `${player} Shows ${formattedCards}\n`;
      });
    }

    formatted += `\nFinal pot size: ${potSize}`;

    if (gameOver || _gameover) {
      const winnerName = winner || _winner;
      if (showdownMode && playersHands[winnerName]) {
        const handValue = holdemhandranker.valueOfHand(
          [
            ...(board.flop || []),
            ...(board.turn ? [board.turn[0]] : []),
            ...(board.river ? [board.river[0]] : [])
          ].filter(card => card !== ''),
          playersHands[winnerName]
        );
        if (handValue) {
          formatted += `\n${winnerName} Wins the pot (${potSize}) with ${handValue.handType} - ${handValue.cards.map(card => {
            const rank = card.slice(0, -1);
            const suit = card.slice(-1);
            return `${rank}${convertSuitToSymbol(suit)}`;
          }).join(' ')}`;
        } else {
          formatted += `\n${winnerName} wins the pot!`;
        }
      } else {
        formatted += `\n${winnerName} wins the pot!`;
      }
    }

    setFormattedHand(formatted);
    return formatted;
  };

  const convertSuitToSymbol = (suit) => {
    switch (suit.toLowerCase()) {
      case 's': return '♠️';
      case 'h': return '♥️';
      case 'd': return '♦️';
      case 'c': return '♣️';
      default: return suit;
    }
  };

  const getStreetEndReason = (street) => {
    if (showdownMode && STREETS.indexOf(street) === STREETS.length - 1) {
      return "Showdown mode - Set all players' cards";
    }
    if (gameOver && STREETS.indexOf(street) > STREETS.indexOf(gameEndedAtStreet)) {
      return `All players folded to ${winner}`;
    }
    return null;
  };

  const isActionDisabled = (action) => {
    if (!heroInfo.position) return true;
    const bigBlind = parseFloat(gameInfo.stakes.split('/')[1]);
    
    if (action === 'call') {
      if (currentStreet === 'preflop' && ['UTG', 'UTG+1', 'UTG+2', 'LJ', 'HJ', 'CO', 'BTN'].includes(currentPlayer)) {
        return false;  // UTG 和之后的位置在翻前可以 call
      }
      
      if (lastBetSize === 0) return true;

      if (currentStreet === 'preflop' && currentPlayer === 'BB') {
        const hasRaiseOrBet = streetActions[currentStreet].some(
          a => (a.action === 'bet' || a.action === 'raise') && a.amount > bigBlind
        );
        if (!hasRaiseOrBet) return true;
      }
    }

    if (action === 'check') {
      // 翻前，UTG 和之后的位置不能 check
      if (currentStreet === 'preflop' && ['UTG', 'UTG+1', 'UTG+2', 'LJ', 'HJ', 'CO', 'BTN'].includes(currentPlayer)) {
        return true;
      }
      
      if (currentStreet === 'preflop' && currentPlayer === 'BB') {
        if (lastBetSize === bigBlind) {
          return false;
        }
      }
      if (currentStreet === 'preflop' && currentPlayer === 'SB') {
        const onlyBlinds = !streetActions[currentStreet].some(
          a => (a.action === 'bet' || a.action === 'raise')
        );
        if (onlyBlinds) return true;
      }
      if (lastBetSize > 0) return true;
    }

    if (action === 'bet') {
      return (lastBetSize > 0);
    }

    if (action === 'raise') {
      return !(lastBetSize > 0);
    }

    return false;
  };

  const handleShare = () => {
    formatHand(gameOver, winner);
    setShowShareModal(true);
  };

  // Get current game state for sharing
  const getCurrentGameState = () => ({
    gameInfo,
    heroInfo,
    streetActions,
    board,
    currentStreet,
    potSize,
    lastBetSize,
    activePlayers,
    lastAggressivePlayer,
    playersHands,
    gameEndedAtStreet
  });

  // 修改 compareHands 函数
  const compareHands = (playersHands, boardCards) => {
    if (!playersHands || Object.keys(playersHands).length === 0) return null;
    
    const communityCards = [
      ...(boardCards.flop || []),
      ...(boardCards.turn ? [boardCards.turn[0]] : []),
      ...(boardCards.river ? [boardCards.river[0]] : [])
    ].filter(card => card !== '');

    if (communityCards.length !== 5) {
      console.error('需要5张公共牌才能比较手牌');
      return null;
    }

    const hands = Object.entries(playersHands).map(([player, cards]) => ({
      id: player,
      cards: cards
    }));

    console.log('Community cards:', communityCards);
    console.log('Player hands:', hands);

    try {
      const winningHands = holdemhandranker.getWinners(communityCards, hands);
      if (winningHands && winningHands.length > 0) {
        // 获取第一名的牌力值
        const firstHandRank = winningHands[0].evalInfo.handRank;
        
        // 找出所有与第一名牌力相同的玩家
        const chopWinners = winningHands.filter(hand => 
          hand.evalInfo.handRank === firstHandRank
        );

        // 如果只有一个赢家或者多个赢家牌力相同，返回他们的ID
        return chopWinners.length === 1 ? chopWinners[0].id : chopWinners.map(hand => hand.id).join(', ');
      }
      return null;
    } catch (error) {
      console.error('比较手牌时出错:', error);
      console.error('Community cards:', communityCards);
      console.error('Player hands:', hands);
      return null;
    }
  };

  // 在 handlePlayerCards 函数中添加手牌比较逻辑
  const handlePlayerCards = (position, cards) => {
    // 保存当前玩家的手牌
    setPlayersHands(prev => ({
      ...prev,
      [position]: cards
    }));

    // 检查是否所有玩家都已选择手牌
    const updatedPlayersHands = {
      ...playersHands,
      [position]: cards
    };

    // 先关闭当前的选择器
    setShowPlayerSelector(false);
    setCurrentCardSelection(null);

    const allPlayersSelected = activePlayers.every(player => 
      updatedPlayersHands[player] && updatedPlayersHands[player].length === 2
    );

    if (allPlayersSelected) {
      // 添加英雄的手牌
      const finalPlayersHands = {
        ...updatedPlayersHands,
        [heroInfo.position]: heroInfo.hand
      };

      // 延迟500ms后再进行比牌
      setTimeout(() => {
        // 比较手牌并找出赢家
        const winner = compareHands(finalPlayersHands, {
          flop: board.flop,
          turn: board.turn,
          river: board.river
        });
        if (winner) {
          // 更新游戏状态
          setGameInfo(prev => ({
            ...prev,
            winner: winner
          }));
          setWinner(winner);
          setGameOver(true);
          formatHand(true, winner);
        }

        // 关闭卡片选择器
        setCurrentCardSelection(null);
        setCurrentShowdownPlayer(null);
      }, 500);
    } else {
      // 找到下一个需要选择手牌的玩家
      const nextPlayer = activePlayers.find(player => 
        !updatedPlayersHands[player] || updatedPlayersHands[player].length !== 2
      );

      // 延迟500ms后显示下一个玩家的选择器
      setTimeout(() => {
        if (nextPlayer) {
          setCurrentShowdownPlayer(nextPlayer);
          setCurrentCardSelection('player');
        }
      }, 500);
    }
  };

  const handleFlopCards = (cards) => {
    setBoard(prev => ({ ...prev, flop: cards }));
    setCurrentCardSelection(null);
    
    // 检查是否所有玩家都all-in
    if (activePlayers.every(player => playerStacks[player] === 0)) {
      // 延迟一下以确保flop牌已经设置完成
      setTimeout(() => {
        setCurrentStreet('turn');
        setCurrentCardSelection('turn');
      }, 500);
    }
  };

  const handleTurnCard = (card) => {
    setBoard(prev => ({ ...prev, turn: card }));
    setCurrentCardSelection(null);
    
    // 检查是否所有玩家都all-in
    if (activePlayers.every(player => playerStacks[player] === 0)) {
      // 延迟一下以确保turn牌已经设置完成
      setTimeout(() => {
        setCurrentStreet('river');
        setCurrentCardSelection('river');
      }, 500);
    }
  };

  const handleRiverCard = (card) => {
    setBoard(prev => ({ ...prev, river: card }));
    setCurrentCardSelection(null);
    
    // 检查是否所有玩家都all-in
    if (activePlayers.every(player => playerStacks[player] === 0)) {
      // 延迟一下以确保river牌已经设置完成
      setTimeout(() => {
        setShowdownMode(true);
        const initialHands = {};
        initialHands[heroInfo.position] = [...heroInfo.hand];
        setPlayersHands(initialHands);

        const otherActivePlayers = activePlayers.filter(p => p !== heroInfo.position);
        if (otherActivePlayers.length > 0) {
          setCurrentShowdownPlayer(otherActivePlayers[0]);
          setCurrentCardSelection('player');
        }
      }, 500);
    }
  };

  useEffect(() => {
    const newUsedCards = [
      ...heroInfo.hand,
      ...board.flop,
      ...board.turn,
      ...board.river
    ].filter(card => card !== '');
    setUsedCards(newUsedCards);
  }, [heroInfo.hand, board]);

  const formatCards = (cards) => {
    if (!cards || cards.length === 0) return '';
    return cards.map(card => {
      const rank = card.rank || card.slice(0, -1);
      const suit = card.suit || card.slice(-1);
      return `${rank}${convertSuitToSymbol(suit)}`;
    }).join(' ');
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 pb-20 md:pb-0">
        <style>{customStyles}</style>
        <style>{animationStyles}</style>
        
        <div className="container max-w-4xl mx-auto px-2 md:px-4 py-3 md:py-6 pb-16">
          <header className="app-header mb-8 relative">
            <div className="absolute top-2 right-2 md:top-3 md:right-3 z-10">
              <LanguageSelector />
            </div>
            <div className="flex flex-col items-center w-full pt-6 md:pt-0">
              <div className="logo-box">
                <div className="logo">
                  <div className="main">{appName.split(' ')[0]}</div>
                  <div className="pro">{appName.split(' ')[1]}</div>
                </div>
              </div>
            </div>
          </header>

          {showSetupModal && (
            <SetupModal
              gameInfo={gameInfo}
              setGameInfo={setGameInfo}
              heroInfo={heroInfo}
              setHeroInfo={setHeroInfo}
              onComplete={handleSetupComplete}
              playerPositions={playerPositions}
            />
          )}

          {setupComplete && (
            <>
              <GameSummaryBar 
                gameInfo={gameInfo} 
                heroInfo={heroInfo} 
                onEdit={() => setShowSetupModal(true)}
                onShare={handleShare}
              />

              <div className="bg-white bg-opacity-90 rounded-lg shadow-lg overflow-hidden mb-6">
                <div className="bg-blue-600 text-white font-semibold px-4 py-3">
                  {t('poker.sections.actionsAndBoard')}
                </div>
                <div className="p-4">
                  <div className="flex border-b mb-4 overflow-x-auto">
                    {STREETS.map((street) => (
                      <button
                        key={street}
                        onClick={() => setCurrentStreet(street)}
                        className={`py-2 px-4 font-medium whitespace-nowrap ${street === currentStreet
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-500 hover:text-gray-700'
                          }`}
                      >
                        {street.charAt(0).toUpperCase() + street.slice(1)}
                      </button>
                    ))}
                  </div>

                  {currentStreet !== 'preflop' && (
                    <div className="bg-green-100 rounded-lg p-4 my-4 border border-green-200 shadow-inner">
                      <div className="flex justify-center items-center min-h-16">
                        <div className="flex mx-2 space-x-1">
                          {board.flop.every(card => card === '') ? (
                            <div className="flex space-x-1">
                              {[0, 1, 2].map(index => (
                                <div
                                  key={`flop-placeholder-${index}`}
                                  className="w-12 h-16 bg-white border border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-100 shadow-sm"
                                  onClick={() => setCurrentCardSelection('flop')}
                                >
                                  {currentStreet === 'flop' && (
                                    <span className="text-gray-500 text-xs">{t('poker.pending')}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex space-x-1 relative group">
                              {board.flop.map((card, index) => (
                                <div
                                  key={`flop-${index}`}
                                  className="w-[36px] h-[50px] rounded-md shadow-md transition-transform transform group-hover:scale-105 border-0"
                                  style={{
                                    backgroundImage: `url(/images/cards/${card}_borderless.svg)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    margin: 0,
                                    padding: 0,
                                  }}
                                />
                              ))}
                              <button
                                className="absolute -top-2 -right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow border border-gray-200"
                                onClick={() => setCurrentCardSelection('flop')}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>

                        {(currentStreet === 'turn' || currentStreet === 'river') && (
                          <div className="mx-2 relative group">
                            {currentStreet === 'turn' && board.turn[0] === '' ? (
                              <div
                                className="w-12 h-16 bg-white border border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-100 shadow-sm"
                                onClick={() => setCurrentCardSelection('turn')}
                              >
                                <span className="text-gray-500 text-xs">{t('poker.pending')}</span>
                              </div>
                            ) : (
                              <div className="relative group">
                                <div
                                  className="w-[36px] h-[50px] rounded-md shadow-md transition-transform transform group-hover:scale-105 border-0"
                                  style={{
                                    backgroundImage: `url(/images/cards/${board.turn[0]}_borderless.svg)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                  }}
                                />
                                <button
                                  className="absolute -top-2 -right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow border border-gray-200"
                                  onClick={() => setCurrentCardSelection('turn')}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {currentStreet === 'river' && (
                          <div className="mx-2 relative group">
                            {board.river[0] === '' ? (
                              <div
                                className="w-12 h-16 bg-white border border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-100 shadow-sm"
                                onClick={() => setCurrentCardSelection('river')}
                              >
                                <span className="text-gray-500 text-xs">{t('poker.pending')}</span>
                              </div>
                            ) : (
                              <div className="relative group">
                                <div
                                  className="w-[36px] h-[50px] rounded-md shadow-md transition-transform transform group-hover:scale-105 border-0"
                                  style={{
                                    backgroundImage: `url(/images/cards/${board.river[0]}_borderless.svg)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                  }}
                                />
                                <button
                                  className="absolute -top-2 -right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow border border-gray-200"
                                  onClick={() => setCurrentCardSelection('river')}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {STREETS.map((street, index) => {
                    if (STREETS.indexOf(street) <= STREETS.indexOf(currentStreet) ||
                      (gameOver && STREETS.indexOf(street) <= STREETS.indexOf(gameEndedAtStreet))) {
                      const streetEndReason = getStreetEndReason(street);
                      return (
                        <div key={street} className={`mb-4 ${street !== currentStreet ? 'hidden' : ''}`}>
                          {streetEndReason ? (
                            <div className="bg-gray-100 border-l-4 border-gray-500 text-gray-700 p-3 rounded-md">
                              <p>{streetEndReason}</p>
                            </div>
                          ) : (
                            street === currentStreet && !gameOver && (
                              <>
                                <div className="bg-blue-50 rounded-md p-2 md:p-3 mb-3 md:mb-4">
                                  <div className="grid grid-cols-2 gap-1 md:gap-2 text-xs md:text-sm">
                                    <div>
                                      <span className="font-medium text-gray-700">{t('poker.currentPlayer')}:</span> {currentPlayer}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">{t('poker.potSize')}:</span> {potSize}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">{t('poker.lastBet')}:</span> {lastBetSize}
                                    </div>

                                    <div>
                                      <span className="font-medium text-gray-700">{t('poker.activePlayers')}:</span>
                                      <span className="hidden md:inline"> {activePlayers.join(', ')}</span>
                                      <span className="inline md:hidden"> {activePlayers.length}人</span>
                                      <button className="inline md:hidden ml-1 text-xs text-blue-500">查看</button>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-5 gap-1 md:gap-2 mb-2 md:mb-4">
                                  {ACTIONS.map(action => {
                                    // 根据当前状态动态确定按钮文本
                                    let buttonText = action.charAt(0).toUpperCase() + action.slice(1);
                                    
                                    // 如果是preflop阶段，且没有任何raise或bet动作
                                    const hasRaiseOrBet = streetActions[currentStreet].some(
                                      a => (a.action === 'bet' || a.action === 'raise')
                                    );
                                    
                                    if (currentStreet === 'preflop' && !hasRaiseOrBet) {
                                      if (action === 'call') buttonText = 'Limp';
                                      if (action === 'raise') buttonText = 'Open';
                                    }
                                    
                                    return (
                                    <button
                                      key={action}
                                      onClick={() => addAction(action)}
                                      disabled={isActionDisabled(action)}
                                      className={`
                                        py-2 px-1 md:px-4 rounded-md font-medium text-white capitalize text-sm md:text-base whitespace-nowrap
                                        transform transition-all duration-200 hover:scale-105 active:scale-95
                                        ${isActionDisabled(action)
                                          ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                          : action === 'fold'
                                            ? 'bg-blue-800 hover:bg-blue-900 shadow-lg hover:shadow-blue-500/30'
                                            : action === 'check'
                                              ? 'bg-green-500 hover:bg-green-600 shadow-lg hover:shadow-green-500/30'
                                              : action === 'call'
                                                ? 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-green-600/30'
                                                : action === 'bet'
                                                  ? 'bg-red-500 hover:bg-red-600 shadow-lg hover:shadow-red-500/30'
                                                  : 'bg-red-600 hover:bg-red-700 shadow-lg hover:shadow-red-600/30'
                                        }
                                      `}
                                    >
                                      {buttonText}
                                    </button>
                                  )})}
                                </div>
                              </>
                            )
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              <div className="bg-white bg-opacity-90 rounded-lg shadow-lg overflow-hidden mb-6">
                <div className="bg-blue-600 text-white font-semibold px-4 py-3 flex justify-between items-center">
                  <h2>{t('poker.sections.actionHistory')}</h2>
                  {isMobile && (
                    <button 
                      onClick={() => setHistoryExpanded(!historyExpanded)}
                      className="text-xs bg-blue-700 hover:bg-blue-800 px-2 py-1 rounded-full"
                    >
                      {historyExpanded ? '收起' : '展开'}
                    </button>
                  )}
                </div>
                <div className="p-4 md:min-h-[200px]">
                  <ActionHistory
                    actions={streetActions}
                    gameInfo={gameInfo}
                    gameOver={gameOver}
                    latestAction={latestAction}
                    compactMode={isMobile && !historyExpanded}
                  />
                </div>
              </div>

              {showdownMode && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                  <div className="bg-blue-600 text-white font-semibold px-4 py-3">
                    Showdown Mode
                  </div>
                  <div className="p-4 bg-blue-50">
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Players' Cards:</h4>
                      {Object.entries(playersHands).map(([player, cards]) => (
                        <div key={player} className="flex items-center mt-2">
                          <span className="w-16 font-medium">{player}:</span>
                          <div className="flex">
                            {cards.map((card, index) => (
                              <div
                                key={index}
                                className="m-1 rounded shadow"
                                style={{
                                  backgroundImage: `url(/images/cards/${card || 'unknown'}_borderless.svg)`,
                                  backgroundPosition: 'center',
                                  width: '42px',
                                  height: '58px',
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {currentShowdownPlayer && (
                      <div className="mb-4">
                        <p className="mb-2">Select cards for player: <strong>{currentShowdownPlayer}</strong></p>
                        <button
                          onClick={() => setShowPlayerSelector(true)}
                          className="w-full p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md mb-2 transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-blue-500/30"
                        >
                          {t('poker.dealMyCards')}
                        </button>
                        {showPlayerSelector && (
                          <div className="w-full">
                            <CardSelector
                              onSelectCards={handlePlayerCards}
                              numCards={2}
                              usedCards={[
                                ...Object.values(playersHands).flat(),
                                ...board.flop,
                                ...board.turn,
                                ...board.river
                              ].filter(card => card !== '')}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {!currentShowdownPlayer && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2">Winner:</h4>
                        <div className="flex flex-col gap-2 bg-green-100 p-3 rounded-lg">
                          {Object.entries(playersHands).map(([player, cards]) => {
                            if (winner && winner.includes(player)) {
                              const handValue = holdemhandranker.valueOfHand(
                                [
                                  ...(board.flop || []),
                                  ...(board.turn ? [board.turn[0]] : []),
                                  ...(board.river ? [board.river[0]] : [])
                                ].filter(card => card !== ''),
                                cards
                              );
                              if (handValue) {
                                return (
                                  <div key={player} className="text-green-700 text-sm">
                                    {winner.includes(',') 
                                      ? `${player} chops the pot (${potSize}) with ${handValue.handType} - ${handValue.cards.map(card => {
                                          const rank = card.slice(0, -1);
                                          const suit = card.slice(-1);
                                          return `${rank}${convertSuitToSymbol(suit)}`;
                                        }).join(' ')}`
                                      : `${player} Wins the pot (${potSize}) with ${handValue.handType} - ${handValue.cards.map(card => {
                                          const rank = card.slice(0, -1);
                                          const suit = card.slice(-1);
                                          return `${rank}${convertSuitToSymbol(suit)}`;
                                        }).join(' ')}`}
                                  </div>
                                );
                              }
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {gameOver && !showdownMode && (
                <div className="bg-white bg-opacity-90 rounded-lg shadow-lg overflow-hidden mb-6">
                  <div className="bg-green-500 text-white font-semibold px-4 py-3">
                    Game Over
                  </div>
                  <div className="p-4 bg-green-50 text-green-800">
                    <p className="font-bold mb-1">Game Over!</p>
                    <p>{winner} Wins the pot of {potSize}!</p>
                  </div>
                </div>
              )}

              <div className="bg-white bg-opacity-90 rounded-lg shadow-lg overflow-hidden mb-6 mt-6">
                <div className="bg-blue-600 text-white font-semibold px-4 py-3">
                  {t('export.exportAndShare')}
                </div>
                <div className="p-4">
                  <ExportSelector
                    actions={streetActions}
                    gameInfo={{
                      ...gameInfo, 
                      winner: winner,
                      showdown: showdownMode ? playersHands : null
                    }}
                    heroInfo={heroInfo}
                    boardCards={board}
                    isMobile={isMobile}
                    formattedText={formattedHand}
                    gameState={getCurrentGameState()}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {currentCardSelection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
              <div className="bg-blue-600 text-white font-semibold px-4 py-3 flex justify-between items-center">
                <h3>
                  {currentCardSelection === 'flop' ? t('poker.selectFlop') :
                    currentCardSelection === 'turn' ? t('poker.selectTurn') : 
                    currentCardSelection === 'river' ? t('poker.selectRiver') :
                    currentCardSelection === 'player' ? `Select cards for ${currentShowdownPlayer}` : ''}
                </h3>
                <button
                  onClick={() => setCurrentCardSelection(null)}
                  className="text-white hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              <div className="p-4">
                <CardSelector
                  onSelectCards={cards => {
                    if (currentCardSelection === 'flop') {
                      handleFlopCards(cards);
                    } else if (currentCardSelection === 'turn') {
                      handleTurnCard(cards);
                    } else if (currentCardSelection === 'river') {
                      handleRiverCard(cards);
                    } else if (currentCardSelection === 'player') {
                      handlePlayerCards(currentShowdownPlayer, cards);
                    }
                  }}
                  numCards={currentCardSelection === 'flop' ? 3 : currentCardSelection === 'player' ? 2 : 1}
                  usedCards={[
                    ...heroInfo.hand,
                    ...(currentCardSelection !== 'flop' ? board.flop : []),
                    ...(currentCardSelection !== 'turn' ? board.turn : []),
                    ...(currentCardSelection !== 'river' ? board.river : []),
                    ...Object.values(playersHands).flat()
                  ].filter(card => card !== '')}
                />
              </div>
            </div>
          </div>
        )}

        {amountModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md overflow-hidden">
              <div className={`${currentAction === 'bet' ? 'bg-blue-600' : 'bg-gradient-to-r from-[#FF0844] to-[#FFB199]'} text-white font-semibold px-4 py-3 flex justify-between items-center`}>
                <h3>
                  {currentAction === 'bet' ? t('poker.betAmount') : t('poker.raiseAmount')}
                </h3>
                <button
                  onClick={() => setAmountModalOpen(false)}
                  className="text-white hover:text-gray-200 focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('poker.enterAmount', { action: currentAction === 'bet' ? t('poker.bet') : t('poker.raise') })}
                  </label>
                  <div className="relative">
                    <input
                      autoFocus
                      type="number"
                      value={actionAmount}
                      onChange={(e) => setActionAmount(e.target.value)}
                      min={currentAction === 'bet' ? "1" : (lastBetSize * 2).toString()}
                      step="0.1"
                      max={playerStacks[currentPlayer]}
                      className={`w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-2 ${currentAction === 'bet' ? 'focus:ring-blue-500 focus:border-blue-500' : 'focus:ring-purple-500 focus:border-purple-500'}`}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none bg-gray-50 h-full rounded-r-md border-l border-gray-300 px-4">
                      <span className="text-gray-500 font-medium">{t('common.unit')}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('poker.suggestedRange')}: {currentAction === 'bet' ? Math.floor(potSize * 0.5) : lastBetSize * 2} - {Math.min(Math.ceil(potSize * 3), playerStacks[currentPlayer])}
                    <br />
                    {t('poker.remainingStack')}: {playerStacks[currentPlayer]}
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('poker.quickAction', { action: currentAction === 'bet' ? t('poker.bet') : t('poker.raise') })}:
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {(currentAction === 'bet' ? [
                      { label: t('poker.potSizeLabels.oneThird', { amount: Math.floor(potSize * 0.33) }), value: Math.floor(potSize * 0.33) },
                      { label: t('poker.potSizeLabels.half', { amount: Math.floor(potSize * 0.5) }), value: Math.floor(potSize * 0.5) },
                      { label: t('poker.potSizeLabels.threeQuarters', { amount: Math.floor(potSize * 0.75) }), value: Math.floor(potSize * 0.75) },
                      { label: t('poker.potSizeLabels.full', { amount: Math.floor(potSize) }), value: Math.floor(potSize) },
                      { label: t('poker.potSizeLabels.oneAndHalf', { amount: Math.floor(potSize * 1.5) }), value: Math.floor(potSize * 1.5) },
                      { label: t('poker.potSizeLabels.double', { amount: Math.floor(potSize * 2) }), value: Math.floor(potSize * 2) }
                    ] : [
                      { label: t('poker.potSizeLabels.minRaise', { amount: lastBetSize * 2 }), value: lastBetSize * 2 },
                      { label: t('poker.potSizeLabels.threeBet', { amount: lastBetSize * 3 }), value: lastBetSize * 3 },
                      { label: t('poker.potSizeLabels.fourBet', { amount: lastBetSize * 4 }), value: lastBetSize * 4 }
                    ]).filter(option => option.value <= playerStacks[currentPlayer]).map((option, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          handleAmountConfirm(option.value);
                          setActionAmount('');
                        }}
                        className={`py-2 px-3 rounded-md text-sm font-medium transition duration-150
                          ${currentAction === 'bet'
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                          }`}
                      >
                        {option.label}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        handleAmountConfirm(playerStacks[currentPlayer]);
                        setActionAmount('');
                      }}
                      className="py-2 px-3 rounded-md text-sm font-medium transition duration-150 bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      {t('poker.allIn')} ({playerStacks[currentPlayer]})
                    </button>
                  </div>
                </div>

                <div className="flex space-x-3 justify-end mt-6">
                  <button
                    onClick={() => setAmountModalOpen(false)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition duration-150 font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      const parsedAmount = parseFloat(actionAmount);
                      if (!isNaN(parsedAmount) && parsedAmount > 0) {
                        handleAmountConfirm(parsedAmount);
                        setActionAmount('');
                      }
                    }}
                    disabled={!actionAmount || isNaN(parseFloat(actionAmount)) || parseFloat(actionAmount) <= 0}
                    className={`py-2 px-4 rounded text-white ${!actionAmount || isNaN(parseFloat(actionAmount)) || parseFloat(actionAmount) <= 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : currentAction === 'bet'
                          ? 'bg-blue-500 hover:bg-blue-600 shadow-md'
                          : 'bg-purple-500 hover:bg-purple-600 shadow-md'
                      }`}
                  >
                    {t('poker.confirmAction', { action: currentAction === 'bet' ? t('poker.bet') : t('poker.raise') })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4 sm:my-8 max-h-[95vh] overflow-hidden animate-fadeIn">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  {t('poker.handShare')}
                </h2>
                <button 
                  onClick={() => {
                    setShowShareModal(false);
                  }}
                  className="p-2 rounded-full hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="关闭"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4 md:p-6">
                <ExportSelector
                  actions={streetActions}
                  gameInfo={{
                    ...gameInfo, 
                    winner: winner,
                    showdown: showdownMode ? playersHands : null
                  }}
                  heroInfo={heroInfo}
                  boardCards={board}
                  isMobile={isMobile}
                  formattedText={formattedHand}
                  gameState={getCurrentGameState()}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <footer className={`bg-gray-800 text-white text-center py-3 text-sm ${isMobile ? '' : 'fixed bottom-0 left-0 right-0 z-10'}`}>
        <div className="container mx-auto px-4">
          <div className="text-gray-300 mb-1">
            © {new Date().getFullYear()} {copyright} • Made with  & 72o
            <img 
              src="/images/pro-logo.jpg" 
              alt="Pro Logo" 
              className="h-4 w-auto inline-block rounded ml-1"
              style={{ 
                objectFit: 'contain',
                opacity: 0.9
              }}
            />
          </div>
          <div className="text-gray-400">
            {t('common.helpAndSupport')}: <a href={`mailto:${window.location.hostname === 'hand.72plus.pro' ? 'SevenDeucePro+handpro' : window.location.hostname === 'fupan.72plus.pro' ? 'SevenDeucePro+fupan' : 'SevenDeucePro+handpro'}@gmail.com`} className="hover:text-white transition-colors">
              {window.location.hostname === 'hand.72plus.pro' ? 'SevenDeucePro+handpro' : window.location.hostname === 'fupan.72plus.pro' ? 'SevenDeucePro+fupan' : 'SevenDeucePro+handpro'} at gmail.com
            </a>
          </div>
        </div>
      </footer>
    </>
  );
};

export default PokerHandFormatter;